import { FeatureFlags } from '~commonInfra/featureFlags/FeatureFlags';
import { ToasterService, ToastType } from '~commonInfra/services/ToasterService';
import { SessionServerSelectors, SessionSiteSelectors, SessionUserSelectors } from '~src/bootstrap/redux/selectors/SessionInfoSelectors';
import { AppState } from '~src/bootstrap/redux/state/AppState';
import { getAppStore } from '~src/bootstrap/redux/store/AppStore';
import { Formatters, Messages } from '~src/codegen/Localize';
import { FlowWithRunPermission } from '~src/flow/serverApi/requests/GetFlowsWithRunPermissionsRequest';
import { FlowRunSpecOutputStepParamsMap } from '~src/flow/serverApi/types/RunFlowTask';
import { SubscriptionFlow, SubscriptionFlowDetails, SubscriptionFlowMetadata } from '~src/flow/serverApi/types/SubscriptionFlow';
import { areFlowParametersEnabled, parameterArrayToMap, parameterMapToArray, parameterParamsArrayToMap } from '~src/flow/utils/FlowParametersUtils';
import { isFlowSelfServiceSchedulesEnabled } from '~src/flow/utils/FlowSelfServiceSchedulesUtils';
import { getEmailRecipientGroupIds, getEmailRecipientUserIds } from '~src/flow/utils/FlowSubscriptionUtil';
import { IResult } from '~src/httpClient/serverApi/types/IResponse';
import { LinkedTaskInfo } from '~src/linkedTasks/models/LinkedTaskInfo';
import { LinkedTaskItem } from '~src/linkedTasks/serverApi/types/LinkedTaskItem';
import { INamedItemInfo } from '~src/spacesPlatform/models/resourceInfoParts';

import { LinkedTaskDialogState, LinkedTaskFlowState, LinkedTaskItemStateType } from '../state/LinkedTaskStateTypes';
import { LinkedTaskErrors } from './LinkedTaskErrors';

export const linkedTaskInitialState: LinkedTaskDialogState = {
  selectedSchedule: '',
  scheduledTaskFrequency: null,
  items: []
};

export function LinkedTaskFlowInitialState(flowId: string, flowName: string, existingId?: string): LinkedTaskFlowState {
  return {
    linkedTaskStepId: existingId,
    flowId,
    flowName,
    outputSelection: {
      selectedOutputs: {},
      isIncrementalMap: {},
    },
    dqw: {
      isSevere: false,
      message: '',
    },
    stopRemainingTasks: true,
    dqwEnabled: false,
    isExpanded: true,
    flowSubscriptions: {
      shouldSendToMe: true,
      shouldIncludeOutputData: false,
      subjectValue: Messages.flowSubscriptionsSubjectDefault({ FLOW_NAME: flowName }),
      messageValue: '',
      shouldShowFirstNRows: false,
      shouldAttachOutputDataToEmail: false,
      shouldIncludeLinkToDatasource: false,
      outputFileFormat: 'CSV',
      selectedOutputForSubscription: null,
      emailRecipients: [],
      subscriberCount: null,
    },
    shouldSendEmailWhenDone: false,
    requiredParameters: [],
  };
}

// Helper function to update a value at a specific index in an array
export function updateInArray<T>(index: number, value: T, array: T[]): T[] {
  return [
    ...array.slice(0, index),
    value,
    ...array.slice(index + 1)
  ];
}
// Insert an item before or after an index in an array
export function insertInArray<T>(index: number, placement: 'before' | 'after', value: T, array: T[]): T[] {
  if (placement === 'before') {
    return [
      ...array.slice(0, index),
      value,
      ...array.slice(index)
    ];
  } else { // after
    return [
      ...array.slice(0, index + 1),
      value,
      ...array.slice(index + 1)
    ];
  }
}

export function isLinkedTaskFlowState(state: LinkedTaskItemStateType): state is LinkedTaskFlowState {
  return (state as any).flowId !== undefined;
}

// Converts front end dialog state for linked task into the linked task backend compatible object
export function convertLinkedTaskState(input: LinkedTaskItemStateType[]): LinkedTaskItem[] {
  const appState: AppState = getAppStore().getState();
  const isDataInEmailBodyEnabled = SessionSiteSelectors.isFlowOutputSubscriptionsDataInEmailBodyEnabled(appState);
  const isDataAsEmailAttachmentEnabled = SessionSiteSelectors.isFlowOutputSubscriptionsDataAsEmailAttachmentEnabled(appState);
  return input.filter(item => isLinkedTaskFlowState(item)).map((item: LinkedTaskFlowState, index: number) => {
    const flowOutputStepIds: string[] = Object.keys(item.outputSelection.selectedOutputs).filter(key => item.outputSelection.selectedOutputs[key]);
    const flowRunSpecOutputStepParamsMap: FlowRunSpecOutputStepParamsMap = {};
    Object.keys(item.outputSelection.selectedOutputs).forEach(key => {
      if (item.outputSelection.selectedOutputs[key] === true) {
        flowRunSpecOutputStepParamsMap[Number(key)] = {
          isIncremental: item.outputSelection.isIncrementalMap && item.outputSelection.isIncrementalMap[key] || item.outputSelection.runAllOption === 'runAllIncremental',
          flowOutputStepId: Number(key)
        };
      }
    });
    let subscriptionFlow: SubscriptionFlow = null;
    if (item.shouldSendEmailWhenDone) {
      const currentUserId = SessionUserSelectors.sessionUserId(getAppStore().getState());
      const userIds = item.flowSubscriptions.shouldSendToMe ?
        getEmailRecipientUserIds(item.flowSubscriptions.emailRecipients, currentUserId)
        : getEmailRecipientUserIds(item.flowSubscriptions.emailRecipients);
      const subscriptionFlowMetadata: SubscriptionFlowMetadata = {
        targetId: item.flowId,
        subject: item.flowSubscriptions.subjectValue,
        message: item.flowSubscriptions.messageValue,
        userIds: userIds,
        subscriberCount: item.flowSubscriptions.subscriberCount
      };
      const subscriptionFlowDetails: SubscriptionFlowDetails = {
        groupIds: getEmailRecipientGroupIds(item.flowSubscriptions.emailRecipients),
        includeOutputData: item.flowSubscriptions.shouldIncludeOutputData,
        outputId: item.flowSubscriptions.selectedOutputForSubscription,
        includeLinkToOutputData: item.flowSubscriptions.shouldIncludeLinkToDatasource,
        showRowsInEmailBody: isDataInEmailBodyEnabled ? item.flowSubscriptions.shouldShowFirstNRows : false,
        attachOutputData: isDataAsEmailAttachmentEnabled ? item.flowSubscriptions.shouldAttachOutputDataToEmail : false,
        attachedOutputDataFormat: isDataAsEmailAttachmentEnabled && item.flowSubscriptions.shouldAttachOutputDataToEmail ? item.flowSubscriptions.outputFileFormat : null,
      };
      subscriptionFlow = {
        subscriptionFlowMetadata: subscriptionFlowMetadata,
        subscriptionFlowDetails: subscriptionFlowDetails
      };
    }

    const parameterValueMap = {
      ...parameterArrayToMap(item.requiredParameters || []),
      ...item.parameterOverrides
    };

    const result: any = {
      linkedTaskStepId: item.linkedTaskStepId, // maybe null
      linkedTaskContentItem: {
        targetId: item.flowId,
        isRunAllIncremental: item.outputSelection.runAllOption === 'runAllIncremental',
        runAllOutputs: item.outputSelection.futureOutputsIncluded === undefined ? true : item.outputSelection.futureOutputsIncluded,
        flowRunSpecOutputStepParamsMap,
        flowOutputStepIds,
        parameterParams: parameterMapToArray(parameterValueMap || {}),
      },
      taskType: 'Flow',
      shouldStopRemainingTasksOnFailure: item.stopRemainingTasks,
      stepNumber: index + 1,
      sendEmailWhenDone: item.shouldSendEmailWhenDone,
      subscriptionFlow: subscriptionFlow
    };

    // Only include data quality warning properties if the Linkedtask tooltip feature flag is disabled
    if (!FeatureFlags.linkedTaskDQWTooltipEnabled) {
      result.shouldAddDataQualityWarning = item.dqwEnabled;
      result.shouldEnableDataQualityWarningHighVisibility = item.dqw.isSevere;
      result.dataQualityWarningMessage = item.dqw.message;
    }

    return result;
  });
}

// Converts LinkedTaskInfo to Front End Linked Task Dialog State
export function convertToLinkedTaskDialogState(input: LinkedTaskInfo): LinkedTaskDialogState {
  const items: LinkedTaskItemStateType[] = input.linkedTaskItems.map(item => {
    if (item === null) {
      return {};
    }
    const selectedOutputs: { [key: string]: boolean } = {};
    const isIncrementalMap: { [key: string]: boolean } = {};
    if (item.runAllOutputs) {
      item.flowOutputSteps.forEach(step => {
        selectedOutputs[step.id] = true;
        isIncrementalMap[step.id] = item.isRunAllIncremental;
      });
    } else {
      for (const key in item.flowRunSpecOutputStepParamsMap) {
        selectedOutputs[key] = true,
        isIncrementalMap[key] = item.flowRunSpecOutputStepParamsMap[key].isIncremental;
      }
    }
    const result: any = {
      linkedTaskStepId: item.linkedTaskStepId,
      flowId: item.id,
      flowName: item.name,
      stopRemainingTasks: item.shouldStopRemainingTasksOnFailure,
      flowSubscriptions: item.subscriptionFlow ? {
        subscriberCount: item.subscriptionFlow.subscriptionFlowMetadata.subscriberCount,
      } : null,
      outputSelection: {
        selectedOutputs: selectedOutputs,
        isIncrementalMap: isIncrementalMap,
        futureOutputsIncluded: item.runAllOutputs,
        runAllOption: item.isRunAllIncremental ? 'runAllIncremental' : 'runAllFull',
        selectAllChecked: item.runAllOutputs,
      },
      parameterOverrides: parameterParamsArrayToMap(item.flowParameterParams || [])
    };

    // Handle data quality warning properties - they might be missing if the feature is enabled
    if (!FeatureFlags.linkedTaskDQWTooltipEnabled && item.shouldAddDataQualityWarning !== undefined) {
      result.dqwEnabled = item.shouldAddDataQualityWarning;
      result.dqw = {
        isSevere: item.shouldEnableDataQualityWarningHighVisibility || false,
        message: item.dataQualityWarningMessage || '',
      };
    } else {
      // Set default values when feature is enabled or properties are missing
      result.dqwEnabled = false;
      result.dqw = {
        isSevere: false,
        message: '',
      };
    }

    return result;
  });

  return {
    linkedTaskId: input.id,
    selectedSchedule: input.schedule.id,
    scheduledTaskFrequency: input.scheduledTaskFrequency,
    items: items
  };
}

export function getLinkedTaskFlowStateFromFlowInfos(flowInfos: INamedItemInfo[], allFlowsContentItems: FlowWithRunPermission[]): LinkedTaskFlowState[] {
  let states: LinkedTaskFlowState[] = [];
  const flowswithPermissions = allFlowsContentItems.filter(flow => flow.userHasRunPermissions === true);
  const allFlowsContentItemsNames = flowswithPermissions.map(flow => flow.name);
  const intersection = flowInfos.filter((info) => info.id && info.name && allFlowsContentItemsNames.includes(info.name));
  intersection.forEach((info) => {
    if (info.id && info.name) {
      states.push(LinkedTaskFlowInitialState(info.id, info.name));
    }
  });
  if (flowInfos.length > intersection.length) {
    const flowsWithoutPermissionsCount = flowInfos.length - intersection.length;
    ToasterService.instance.pop({
      type: ToastType.Warning,
      text: Messages.linkedTasksWarningPermissionsToScheduleFlow({ FLOW_COUNT: flowsWithoutPermissionsCount.toString() }),
    });
  }
  return states;
}


export function createTaskEnabled(state: LinkedTaskDialogState): boolean {
  if(isFlowSelfServiceSchedulesEnabled() && !state.scheduledTaskFrequency) return false;
  const linkedTaskItems = state.items.filter(item => isLinkedTaskFlowState(item));
  if (linkedTaskItems.length > 0 && linkedTaskItems.length === state.items.length) {
    if (areFlowParametersEnabled()) {
      let anyUnsetParameters = false;
      (linkedTaskItems as LinkedTaskFlowState[]).forEach(item => {
        item.requiredParameters && item.requiredParameters.forEach(parameter => {
          if (!item.parameterOverrides || !item.parameterOverrides[parameter.id]) {
            anyUnsetParameters = true;
          }
        });
      });
      return !anyUnsetParameters;
    }
    return true;
  }
  return false;
}

// Get the first valid flow id in a list of linked task items, otherwise return null
export function getFirstFlowId(items: LinkedTaskItemStateType[]): string {
  const firstFlowItem = items.find(item => isLinkedTaskFlowState(item));
  return firstFlowItem && isLinkedTaskFlowState(firstFlowItem) ? firstFlowItem.flowId : null;
}

export function displayLinkedTaskToasts(result: IResult, successMsg?: string) {
  if (result.errors && result.errors.length > 0) {
    ToasterService.instance.pop({
      type: ToastType.Error,
      text: LinkedTaskErrors.errorMessageFor(result.errors[0].code)
    });
  } else if (successMsg) {
    ToasterService.instance.pop({
      type: ToastType.Info,
      text: successMsg
    });
  }
}

// Checks if the instance is enabled on server or is online, and if the setting is enabled on the site, or is a single server-site
export function areLinkedTasksEnabled(inServerArea?: boolean): boolean {
  const state = getAppStore().getState();
  const doesTheServerHaveMoreThanOneSite = SessionServerSelectors.doesTheServerHaveMoreThanOneSite(state);
  const isEnabledOnServerOrOnline = SessionServerSelectors.isOnlineEnvironment(state) || SessionServerSelectors.linkedTasksSchedulingEnabled(state);
  const isEnabledOnSite = SessionSiteSelectors.isLinkedTaskSchedulingOnSiteEnabled(state);
  return isEnabledOnServerOrOnline && (inServerArea || isEnabledOnSite || !doesTheServerHaveMoreThanOneSite);
}

// Find the first flow id upstream or downstream of an index, or return null
export function findFlowIdFromLinkedTasks(items: LinkedTaskItemStateType[], index: number, direction: 'upstream' | 'downstream'): string | null {
  let arr: LinkedTaskItemStateType[];
  // Look upstream or downstream from the index
  if (direction === 'upstream') {
    arr = items.slice(0, index);
    arr.reverse();
  } else {
    arr = items.slice(index);
  }
  const flow = arr.find(item => isLinkedTaskFlowState(item));
  return flow !== undefined ? (flow as LinkedTaskFlowState).flowId : null;
}

export const maximumLinkedTasks = 20;

export function displayMaximumLinkedTaskMessage() {
  ToasterService.instance.pop({ type: ToastType.Error, text: Messages.linkedTasksMaximumTasksMessage({ MAXIMUM_COUNT_LOC: Formatters.formatCount(maximumLinkedTasks) }) });
}

export function displayContentActionNotificationForLinkedTasks(): void {
  ToasterService.instance.pop({ type: ToastType.Error, text: Messages.linkedTasksRunWithFlowTasksError() });
}
